import React, { useState, useCallback, useEffect } from 'react';
import { UserPlus, LayoutList, Sparkles, Handshake, LogOut } from 'lucide-react';
import { PortalHeader, PortalBottomNav, NavItem } from '../shared/PortalChrome';
import PartnerView from '../../components/PartnerView';
import PartnerPitch from './PartnerPitch';
import PartnerLogin from './PartnerLogin';
import {
  ReferralApplyForm, ContractPending, ReferralSubmitForm, ReferralDashboard, ActiveBadge,
} from './ReferralViews';
import { useLanguage } from '../../components/LanguageContext';
import '../shared/portals.css';

// ============================================================
// PartnerPortal — the Referral Partner programme, and the front door for
// anyone who wants to introduce buyers to InvestMenter.
//
// THE LIFECYCLE, which is what this component is really modelling:
//
//   pitch  -> apply -> (we email the agreement) -> they sign
//                                                     |
//                                                     v
//          sign in with an emailed code -> refer people -> watch them
//
// The two halves have different audiences and neither should see the other's
// screens: a stranger needs the case for joining and an application form; an
// active partner needs the referral form and their dashboard.
//
// COOPERATION PARTNER is no longer the front door but has NOT been retired —
// approved VVIP partners still reach their commission statements through a
// tab that only appears for them. Removing it would have taken away live
// commission records from people already earning.
//
// The contract gate is enforced server-side (referralRoutes' requirePartner),
// not here. This component only decides what to SHOW; it cannot grant access.
// ============================================================

const ACCENT = '#B99A4C';
const API_URL =
  (process.env.REACT_APP_API_URL ||
    'https://investmenter-backend-1081814658115.europe-west1.run.app') + '/api';

const COOP_STATUS = 'VVIP - Cooperation Partner';

interface Props {
  onChangeRole?: () => void;
}

const readSession = (): any | null => {
  try {
    const token = localStorage.getItem('authToken');
    const raw = localStorage.getItem('investor');
    if (!token || !raw) return null;
    const investor = JSON.parse(raw);
    return investor && investor.email ? investor : null;
  } catch {
    return null;
  }
};

// Where an unauthenticated visitor is in the funnel. Remembered per browser so
// someone who applies, closes the app and comes back is not shown the pitch
// again as though nothing happened.
const APPLIED_KEY = 'referralApplied';

type Stage = 'pitch' | 'apply' | 'pending' | 'login';

const PartnerPortal: React.FC<Props> = ({ onChangeRole }) => {
  const { t, language } = useLanguage();
  const [investor, setInvestor] = useState<any | null>(readSession);
  const [stage, setStage] = useState<Stage>('pitch');
  const [applied, setApplied] = useState<{ email: string; status: string } | null>(null);
  const [tab, setTab] = useState('refer');

  // Partner standing, straight from the server. `null` while unknown.
  const [partnerState, setPartnerState] = useState<
    { active: true } | { active: false; status: string } | null
  >(null);
  const [reloadKey, setReloadKey] = useState(0);

  const token = (() => { try { return localStorage.getItem('authToken') || ''; } catch { return ''; } })();

  useEffect(() => {
    try {
      const raw = localStorage.getItem(APPLIED_KEY);
      if (raw) { const v = JSON.parse(raw); if (v?.email) { setApplied(v); setStage('pending'); } }
    } catch { /* ignore */ }
  }, []);

  // One call answers both "are they a partner" and "have they signed" — the
  // dashboard endpoint refuses with a specific error code when they haven't.
  useEffect(() => {
    if (!investor || !token) { setPartnerState(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/referral/my-referrals`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (cancelled) return;
        if (data.success) setPartnerState({ active: true });
        else setPartnerState({ active: false, status: data.status || data.error || 'not_a_partner' });
      } catch {
        if (!cancelled) setPartnerState({ active: false, status: 'unknown' });
      }
    })();
    return () => { cancelled = true; };
  }, [investor, token, reloadKey]);

  const signIn = useCallback((next: any) => {
    setInvestor(next);
    setPartnerState(null);
    setTab('refer');
  }, []);

  const signOut = useCallback(() => {
    try {
      ['authToken', 'investor', 'investorId', 'investorEmail', 'lastActivity'].forEach((k) =>
        localStorage.removeItem(k),
      );
    } catch { /* ignore */ }
    setInvestor(null);
    setPartnerState(null);
    setStage('pitch');
  }, []);

  const onApplied = useCallback((email: string, status: string) => {
    const v = { email, status };
    try { localStorage.setItem(APPLIED_KEY, JSON.stringify(v)); } catch { /* ignore */ }
    setApplied(v);
    setStage('pending');
  }, []);

  // ---------------- Signed out ----------------
  if (!investor) {
    if (stage === 'apply') {
      return <ReferralApplyForm onApplied={onApplied} onCancel={() => setStage('pitch')} />;
    }
    if (stage === 'login') {
      return (
        <PartnerLogin
          onSignedIn={signIn}
          onBack={() => setStage(applied ? 'pending' : 'pitch')}
          onChangeRole={onChangeRole}
        />
      );
    }
    if (stage === 'pending' && applied) {
      return (
        <ContractPending
          email={applied.email}
          status={applied.status}
          onSignIn={() => setStage('login')}
        />
      );
    }
    return (
      <PartnerPitch
        onApply={() => setStage('apply')}
        onSignIn={() => setStage('login')}
        onBack={onChangeRole}
        ctaKey="rfApplyCta"
      />
    );
  }

  // ---------------- Signed in ----------------
  const isCoop = investor.vipStatus === COOP_STATUS;
  const isActivePartner = partnerState?.active === true;

  const NAV: NavItem[] = [
    ...(isActivePartner
      ? [
          { id: 'refer', label: 'rfNavRefer', icon: UserPlus },
          { id: 'referrals', label: 'rfNavReferrals', icon: LayoutList },
        ]
      : []),
    { id: 'programme', label: 'pnProgramme', icon: Sparkles },
    // Only for someone who actually holds the status — an ordinary referral
    // partner has no commission statements and should not see an empty tab.
    ...(isCoop ? [{ id: 'cooperation', label: 'pnCooperation', icon: Handshake }] : []),
  ];

  // Never leave the nav pointing at a tab that isn't rendered.
  const activeTab = NAV.some((n) => n.id === tab) ? tab : NAV[0]?.id || 'programme';

  return (
    <div className="pt-screen">
      <PortalHeader
        title={t('rfPartnerTitle')}
        subtitle={investor.name || investor.email}
        accent={ACCENT}
        onChangeRole={onChangeRole}
        right={
          <button
            onClick={signOut}
            title={t('pnSignOut')}
            aria-label={t('pnSignOut')}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: '#f3f4f6', border: 'none', borderRadius: 10,
              padding: '8px 10px', color: '#6b7280', cursor: 'pointer',
            }}
          >
            <LogOut size={15} />
          </button>
        }
      />

      <div className="pt-fade">
        {isActivePartner && <ActiveBadge />}

        {/* Signed in but the agreement isn't signed: say so plainly rather
            than showing referral tools that the server would refuse. */}
        {partnerState && !partnerState.active && activeTab === 'programme' && (
          <ContractPending
            email={investor.email}
            status={partnerState.status === 'contract_incomplete' ? 'contract_sent' : partnerState.status}
            onSignIn={() => setStage('apply')}
          />
        )}

        {activeTab === 'refer' && isActivePartner && (
          <ReferralSubmitForm token={token} onSubmitted={() => setReloadKey((k) => k + 1)} />
        )}
        {activeTab === 'referrals' && isActivePartner && (
          <ReferralDashboard token={token} reloadKey={reloadKey} />
        )}
        {activeTab === 'programme' && (!partnerState || partnerState.active) && (
          <PartnerPitch
            embedded
            onApply={() => setTab('refer')}
            ctaKey={isActivePartner ? 'rfNavRefer' : 'rfApplyCta'}
          />
        )}
        {activeTab === 'cooperation' && (
          <PartnerView
            partnerType="cooperation"
            currentInvestor={investor}
            vipStatus={investor.vipStatus || null}
            apiUrl={API_URL.replace('/api', '')}
            language={language}
          />
        )}
      </div>

      <PortalBottomNav
        items={NAV.map((n) => ({ ...n, label: t(n.label) }))}
        active={activeTab}
        onChange={setTab}
        accent={ACCENT}
      />
    </div>
  );
};

export default PartnerPortal;
